import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  //Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  TextField,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import EditHwmsCodeModal from "./EditHwmsCodeModal";
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
}));

function HealthFacilities() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [healthUnits, setHealthUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const unitsPerPage = 4;
  //const PRIMARY_COLOR = "#0a2e52";

  useEffect(() => {
    fetchHealthUnits();
  }, []);

  const fetchHealthUnits = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/health-units");
      const data = await res.json();
      setHealthUnits(data.recordset || data);
    } catch (error) {
      console.error("Error fetching health units:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async ({ entity, unit, hwmsCode }) => {
    await fetch(`/api/support/health-units/${selectedUnit.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity, unitName: unit, hwmsCode }),
    });
    fetchHealthUnits();
  };

  const filteredUnits = healthUnits.filter((unit) => {
    const search = searchTerm.toLowerCase();

    return (
      String(unit.unitName).toLowerCase().includes(search) ||
      String(unit.Entity).toLowerCase().includes(search) ||
      String(unit.hwmsCode).toLowerCase().includes(search)
    );
  });
  const indexOfLast = currentPage * unitsPerPage;
  const indexOfFirst = indexOfLast - unitsPerPage;
  const currentUnits = filteredUnits.slice(indexOfFirst, indexOfLast);
  const handleMenuOpen = (event, unit) => {
    setAnchorEl(event.currentTarget);
    setSelectedUnit(unit);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    if (!editModalOpen) setSelectedUnit(null);
  };

  const handleEdit = () => {
    setEditModalOpen(true);
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Health Units
        </Typography>

        <TextField
          size="small"
          placeholder="Search by unit name, entity..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          sx={{ minWidth: 250 }}
        />
      </Box>

      {/* Table */}
      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": { color: "white", fontWeight: 600 },
              }}
            >
              <ModernTableCell>ID</ModernTableCell>
              <ModernTableCell>Unit Name</ModernTableCell>
              <ModernTableCell>Entity</ModernTableCell>
              <ModernTableCell>HWMS Code</ModernTableCell>
              <ModernTableCell>Actions</ModernTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Loading health units...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentUnits.length > 0 ? (
              currentUnits.map((unit, index) => (
                <StyledTableRow key={index}>
                  <ModernTableCell>{indexOfFirst + index + 1}</ModernTableCell>
                  <ModernTableCell sx={{ fontWeight: 600 }}>
                    {unit.unitName}
                  </ModernTableCell>
                  <ModernTableCell>{unit.Entity}</ModernTableCell>
                  <ModernTableCell>{unit.hwmsCode || "N/A"}</ModernTableCell>
                  <ModernTableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, unit)}>
                      <MoreVertIcon />
                    </IconButton>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={5} align="center">
                  No health units found
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredUnits.length / unitsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
        />
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" />
          <Typography ml={1}>Edit HWMS CODE</Typography>
        </MenuItem>
      </Menu>
      <EditHwmsCodeModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUnit(null); 
        }}
         onSaveSuccess={fetchHealthUnits}
        onSave={handleSave}
        unit={selectedUnit}
      />
    </Box>
  );
}

export default HealthFacilities;
